<template>
  <div>
    <md-button v-if="minimized" class="md-icon-button md-dense refresh-btn" @click="toggleIsActive">
      <md-tooltip md-direction="top">Table settings</md-tooltip>
      <md-icon>settings</md-icon>
    </md-button>

    <div :class="minimized ? 'menu' : ''" v-if="isActive || !minimized">
      <div style="padding: 0 8px">
        <md-field>
          <label>Search</label>
          <md-input ref="searchInp" v-model="searchValue"></md-input>
          <md-button class="md-icon-button md-dense" @click="searchValue = ''" v-if="searchValue">
            <md-icon class="clear-search">clear</md-icon>
          </md-button>
        </md-field>
      </div>
      <md-checkbox v-model="availableLogColumnsToggle" @change="changeAvailableLogColumnsToggle"
        >Show all columns with data</md-checkbox
      >
      <!--  menu part  -->
      <div v-if="mode === 'switch'">
        <md-list class="settings-list">
          <md-list-item v-for="col in visibleCols" :key="col.name">
            <span class="md-list-item-text">{{ col }}</span>
            <md-switch v-model="settings[col]" />
          </md-list-item>
        </md-list>
      </div>
      <div v-else class="md-layout">
        <div>
          <md-subheader>Available columns:</md-subheader>
          <md-list class="settings-list">
            <draggable
              class="draggable-container"
              group="people"
              v-model="availableList"
              :disabled="availableLogColumnsToggle"
            >
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <template v-for="col in availableList">
                  <md-list-item
                    v-if="showDraggableItem(col.name)"
                    class="draggable-item"
                    :key="col.name"
                    :disabled="availableLogColumnsToggle"
                  >
                    <span class="md-list-item-text">{{ col.name }}</span>
                  </md-list-item>
                </template>
              </transition-group>
            </draggable>
          </md-list>
        </div>
        <div>
          <md-subheader style="display: flex; justify-content: space-between">
            Selected columns:
            <md-button
              @click="$emit('resetDefault')"
              class="md-icon-button md-dense"
              :disabled="availableLogColumnsToggle"
            >
              <md-tooltip md-direction="top">Reset To Default</md-tooltip>
              <md-icon :class="disableButton">delete_sweep</md-icon>
            </md-button>
          </md-subheader>
          <md-list class="settings-list">
            <draggable
              class="draggable-container"
              group="people"
              v-model="selectedList"
              @change="onSelectChange"
              :disabled="availableLogColumnsToggle"
            >
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <template v-for="col in selectedList">
                  <md-list-item
                    v-if="showDraggableItem(col.name)"
                    class="draggable-item"
                    :key="col.name"
                    :disabled="availableLogColumnsToggle"
                  >
                    <span class="md-list-item-text">{{ col.name }}</span>
                  </md-list-item>
                </template>
              </transition-group>
            </draggable>
          </md-list>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { ref, computed, watch } from "@vue/composition-api";

export default {
  name: "TableSettings",
  components: { draggable },
  props: {
    cols: {
      type: Array,
      default: () => [],
    },
    defaultCols: {
      type: Array,
      default: () => [],
    },
    minimized: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "drag",
    },
    forceClose: {
      type: Boolean,
      default: false,
    },
    availableLogColumnsToggle: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const isActive = ref(false);
    watch(
      () => props.forceClose,
      (newValue, oldValue) => {
        if (newValue) {
          isActive.value = false;
          emit("close");
        }
      }
    );
    const toggleIsActive = () => {
      isActive.value = !isActive.value;
      if (isActive.value) {
        emit("open");
      } else {
        emit("close");
      }
    };

    const searchInArray = (searchString, array) => {
      return array.filter((item) => {
        return item.includes(searchString);
      });
    };
    const searchValue = ref("");

    // for switch mode
    const visibleCols = computed(() => {
      return searchInArray(searchValue.value, props.cols);
    });

    const setSettings = (cols) => {
      let result = {};
      cols.forEach((item) => {
        result[item] = true;
      });
      return result;
    };
    const settings = ref(setSettings(props.cols));

    // Draggable mode
    const drag = ref(false);
    const createDraggableList = (colsArray) =>
      colsArray.map((item, index) => {
        return {
          name: item,
          fixed: false,
        };
      });

    const availableList = ref(createDraggableList(props.cols.filter((item) => !props.defaultCols.includes(item))));
    const selectedList = ref(createDraggableList(props.defaultCols));
    watch(
      () => props.defaultCols,
      (oldValue, newValue) => {
        availableList.value = createDraggableList(props.cols.filter((item) => !props.defaultCols.includes(item)));
        selectedList.value = createDraggableList(props.defaultCols);
      }
    );

    const showDraggableItem = (itemName) => {
      return searchValue ? itemName.toUpperCase().includes(searchValue.value.toUpperCase()) : true;
    };
    const onSelectChange = () => {
      emit(
        "saved",
        selectedList.value.map((item) => item.name)
      );
    };

    const onSave = () => {
      let result = settings.value;
      for (const property in result) {
        if (!result[property]) {
          delete result[property];
        }
      }
      emit("saved", Object.keys(result));
    };

    const disableButton = computed(()=> {
      return props.availableLogColumnsToggle ? "button-disabled" : null;
    });

    const changeAvailableLogColumnsToggle = () => {
      emit("changeAvailableLogColumnsToggle");
      window.localStorage.setItem("availableLogColumnsToggle", props.availableLogColumnsToggle);
    };

    return {
      isActive,
      toggleIsActive,
      visibleCols,
      searchValue,
      settings,
      drag,
      availableList,
      selectedList,
      showDraggableItem,
      onSelectChange,
      onSave,
      disableButton,
      changeAvailableLogColumnsToggle,
    };
  },
};
</script>

<style lang="scss">
.menu {
  position: absolute;
  background-color: white;
  color: black;
  padding: 8px;
  z-index: 10;
  right: 70px;
  border-radius: 4px;
  box-shadow: 0 12px 20px -10px rgba(156, 39, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12),
    0 7px 8px -5px rgba(156, 39, 176, 0.2);
}

.settings-list {
  min-width: 16vw;
  max-height: 400px;
  overflow-y: auto;

  .md-ripple > span {
    position: relative !important;
  }
}

.draggable-item {
  margin: 4px 0;
  cursor: grab;
  border: 1px solid rgba(0, 0, 0, 0.125);
  position: relative;
  border-radius: 4px;
  background-color: #fff;
}

.draggable-item:hover {
  background-color: #ecebeb;
}

.draggable-item:active {
  cursor: grabbing;
}

.draggable-container {
  height: 100%;
  padding: 0 4px;

  & > span {
    display: block;
    width: 100%;
    min-height: 300px;
  }
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.button-disabled {
  color: #e2e2e2 !important;
}
</style>
